import { JobPhaseCostItem } from "generated/graphql";
import { CustomJobPhase } from "hooks/jobs/job-phases/useGetJobPhase";
import * as z from "zod";
import { pushIfNotExists } from "helpers/functions";

export const schema = z.object({
  jobPhaseId: z.string().min(1, "Job phase id is required"),
  plantId: z.string().optional(),
  plantProductId: z.string().optional(),
  schedules: z.array(
    z.object({
      id: z.string().min(1, "Schedule id is required"),
      estimatedMaterialOverride: z.number().optional(),
    })
  ),
  equipmentPieces: z.array(
    z.object({
      id: z.string().min(1, "Equipment Piece Id is Required"),
      name: z.string().optional(),
    })
  ),
  crews: z.array(
    z.object({
      id: z.string().min(1, "Crew Id is Required"),
      name: z.string().optional(),
    })
  ),
  transferTrucks: z.array(
    z.object({
      id: z.string().min(1, "Transfer truck Id is Required"),
      quantity: z.number().nullable().optional(),
      proposalPlantProductId: z.string().optional(),
      workOrderTruckId: z.string().optional(),
    })
  ),
});

export type UpdateWorkOrderInputSchema = z.infer<typeof schema>;

export function cleanUpdateWorkOrderInput(values: UpdateWorkOrderInputSchema) {
  const cleanParams = {
    ...values,
    jobPhaseId: values.jobPhaseId,
    crews: values.crews.map((e) => ({
      id: e.id,
      name: e.name,
    })),
    schedules: values.schedules?.map((e) => ({
      id: e.id,
      estimatedMaterialOverride: e.estimatedMaterialOverride,
    })),
    equipmentPieces: values.equipmentPieces.map((e) => ({
      id: e.id,
      name: e.name,
    })),
    transferTrucks: values.transferTrucks?.map((e) => ({
      id: e.id,
      workOrderTruckId: e.workOrderTruckId,
      quantity: e.quantity,
      proposalPlantProductId: e.proposalPlantProductId,
    })),
  };

  return cleanParams;
}

export const getDefaultValues = ({
  jobPhase,
  costItem,
}: {
  jobPhase: CustomJobPhase;
  costItem?: JobPhaseCostItem;
}) => {
  const product = jobPhase?.proposalProduct;
  const plantId =
    jobPhase?.jobPhasePlant?.plant?.id || jobPhase?.proposalProduct?.plantProduct?.plant?.plantId;
  const plantProductId =
    jobPhase?.jobPhasePlant?.plantProduct?.id ||
    jobPhase?.proposalProduct?.plantProduct?.plantProductId;

  const equipmentPieces = jobPhase?.workOrderEquipmentPieces?.length
    ? pushIfNotExists(
        jobPhase?.workOrderEquipmentPieces,
        costItem?.equipmentPieces ?? [],
        "equipmentPieceId"
      )
    : jobPhase?.proposalProduct?.equipmentPieces;
  const crews = jobPhase?.workOrderCrews?.length
    ? jobPhase?.workOrderCrews
    : jobPhase?.proposalProduct?.crews;

  const transferTrucks = jobPhase?.workOrderTransferTrucks || [];
  return {
    jobPhaseId: jobPhase.id,

    equipmentPieces: equipmentPieces?.map((equipmentPiece) => ({
      id: equipmentPiece?.equipmentPieceId ?? equipmentPiece?.id,
      name: equipmentPiece.name,
    })),
    schedules:
      jobPhase?.scheduleBlocks?.map((schedule) => ({
        id: schedule.id,
        estimatedMaterialOverride: schedule.estimatedMaterialOverride || schedule.estimatedMaterial,
      })) ?? [],
    crews:
      crews?.map((crew) => ({
        id: crew?.crewId ?? crew.id,
        name: crew.name,
      })) ?? [],
    transferTrucks:
      transferTrucks?.map((truck) => ({
        id: truck?.transferTruckId ?? truck?.id,
        workOrderTruckId: truck?.id,
        quantity: truck?.quantity ?? (jobPhase.worksheet ? null : 0),
        proposalPlantProductId: truck?.proposalPlantProductId,
      })) ?? [],
    plantId,
    plantProductId,
  };
};
